import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import '../styles/product.css';
import Cookies from 'js-cookie';
import Notification from '../components/Notification';
import 'swiper/swiper-bundle.css';
import PremiumCarousel from '../components/PremiumCarousel';
import { CarouselSkeleton, ProductGridSkeleton } from '../components/Skeletons';
import ProductsGrid from '../components/ProductsGrid';
import ProductFilters from '../components/ProductFilters';
import { filterPromotedProducts } from '../utils/promotionUtils';
import { handleLikeClick as likeHandler } from '../utils/likeHandler';
import { UserContext } from '../contexts/UserContext';

const fetchProductsByUserId = async (userId, page, pageSize) => {
    try {
        const response = await fetch(`${config.backendUrl}/products/user/${userId}/?page=${page}&pageSize=${pageSize}`);
        if (response.ok) {
            const data = await response.json();
            // Map products to ensure structure matches ProductsGrid expectations
            const mappedResults = (data.results || []).map(product => ({
                id: product.id,
                uuid: product.uuid,  // Add UUID field
                name: product.name || '',
                description: product.description || '',
                price: product.price || 0,
                currency: product.currency || 'XAF',
                category_name: product.category_name || (product.category && product.category.name ? product.category.name : ''),
                city_name: product.city_name || (product.city && product.city.name ? product.city.name : ''),
                condition: product.condition || '',
                likes: product.likes || 0,
                views: product.views || 0,
                promote_type: product.promote_type || null,
                negotiable: product.negotiable || false,
                first_image: product.first_image || (product.images && product.images.length > 0 ? (product.images[0].url || product.images[0].image) : null),
                images: product.images || [],
                liked_by: product.liked_by || [],
                owner_name: product.owner_name || (product.owner && product.owner.name ? product.owner.name : ''),
                date_created: product.date_created || null,
            }));
            return { ...data, results: mappedResults };
        } else {
            console.error('Failed to fetch products');
            return { results: [], next: null };
        }
    } catch (error) {
        console.error('An error occurred while fetching products:', error);
        return { results: [], next: null };
    }
};

const ProductsUser = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { redirectToLogin } = useContext(UserContext);
    // cities context removed as it was not being used
    
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(false); // Added state for loading more products
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [promotedProducts, setPromotedProducts] = useState([]);
    const [loggedInUser, setLoggedInUser] = useState(null);
    // likedProducts removed as it was not being used
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    
    // Filter state
    const [filters, setFilters] = useState({
        sortBy: '',
        condition: '',
        minPrice: '',
        maxPrice: '',
        city: '',
        negotiable: false,
        promotedOnly: false
    });
    const [allProducts, setAllProducts] = useState([]); // Store unfiltered products
    const [filteredProducts, setFilteredProducts] = useState([]); // Store filtered products
    
    const pageSize = 30; // Increased from 10 to 30 for better performance

    // Filter products based on current filters
    const applyFilters = useCallback((productsToFilter, currentFilters) => {
        let filtered = [...productsToFilter];

        // Condition filter
        if (currentFilters.condition) {
            filtered = filtered.filter(product => 
                product.condition && product.condition.toLowerCase() === currentFilters.condition.toLowerCase()
            );
        }

        // Price range filter
        if (currentFilters.minPrice || currentFilters.maxPrice) {
            filtered = filtered.filter(product => {
                const price = parseFloat(product.price) || 0;
                const min = parseFloat(currentFilters.minPrice) || 0;
                const max = parseFloat(currentFilters.maxPrice) || Infinity;
                return price >= min && (max === Infinity || price <= max);
            });
        }

        // City filter
        if (currentFilters.city) {
            filtered = filtered.filter(product => 
                product.city_name && product.city_name.toLowerCase() === currentFilters.city.toLowerCase()
            );
        }

        // Negotiable filter
        if (currentFilters.negotiable) {
            filtered = filtered.filter(product => product.negotiable === true);
        }

        // Promoted only filter
        if (currentFilters.promotedOnly) {
            filtered = filterPromotedProducts(filtered);
        }

        // Sort products
        if (currentFilters.sortBy) {
            switch (currentFilters.sortBy) {
                case 'newest':
                    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    break;
                case 'oldest':
                    filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    break;
                case 'price_low':
                    filtered.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
                    break;
                case 'price_high':
                    filtered.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
                    break;
                case 'most_liked':
                    filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                    break;
                case 'most_viewed':
                    filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
                    break;
                default:
                    break;
            }
        }

        return filtered;
    }, []);

    // Handle filter changes
    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    // Apply filters when filters or allProducts change
    useEffect(() => {
        if (allProducts.length > 0) {
            const filtered = applyFilters(allProducts, filters);
            setFilteredProducts(filtered);
            setProducts(filtered); // Update displayed products
        }
    }, [filters, allProducts, applyFilters]);

    useEffect(() => {
        const getLoggedInUser = async () => {
            const token = Cookies.get('authToken');
            if (token) {
                try {
                    const response = await fetch(`${config.backendUrl}/users/me/`, {
                        headers: {
                            Authorization: `Token ${token}`,
                        },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setLoggedInUser(data);
                    } else {
                        console.error('Failed to fetch logged-in user');
                    }
                } catch (error) {
                    console.error('Error fetching logged-in user:', error);
                }
            }
        };

        const getProducts = async () => {
            setLoading(true);
            const data = await fetchProductsByUserId(userId, 1, pageSize);
            setAllProducts(data.results); // Store unfiltered products
            setFilteredProducts(data.results); // Initialize filtered products
            setProducts(data.results);
            setHasMore(data.next !== null);
            setLoading(false);
        };

        const fetchPromotedProducts = async () => {
            try {
                const response = await fetch(`${config.backendUrl}/products/promoted/user/${userId}/`, {
                    method: 'GET',
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch user promoted products');
                }

                const data = await response.json();
                setPromotedProducts(data.results || []);
            } catch (err) {
                console.error('Error fetching user promoted products:', err);
                setPromotedProducts([]);
            }
        };

        getLoggedInUser();
        getProducts();
        fetchPromotedProducts();
    }, [userId]);

    const fetchMoreData = async () => {
        if (!hasMore || loadingProducts) return;

        setLoadingProducts(true); // Set loading state to true
        const nextPage = page + 1;

        try {
            const data = await fetchProductsByUserId(userId, nextPage, pageSize);

            if (data.results && data.results.length > 0) {
                setAllProducts((prevProducts) => {
                    // Append new products to the existing ones
                    const uniqueProducts = [...prevProducts, ...data.results].filter(
                        (product, index, self) =>
                            index === self.findIndex((p) => p.id === product.id) // Ensure no duplicates
                    );
                    return uniqueProducts;
                });
                // filteredProducts will be updated by the useEffect
                setPage(nextPage);
                setHasMore(data.next !== null);
            } else {
                setHasMore(false); // No more data to fetch
            }
        } catch (error) {
            console.error('Error fetching more products:', error);
        } finally {
            setLoadingProducts(false); // Set loading state to false
        }
    };

    const handleLike = async (productId, isLikedByUser) => {
        await likeHandler({
            productId,
            isLikedByUser,
            loggedInUser,
            setLikedProducts: () => {}, // No-op function since we manage likes through products state
            setProducts,
            navigate,
            setNotification,
            redirectToLogin,
        });
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '' });
    };

    const handleProductClick = (productUuid, event) => {  // Changed to productUuid, added event
        // Add animation effect
        const card = event?.currentTarget;
        if (card) {
            card.classList.add('clicked');
            setTimeout(() => {
                card.classList.remove('clicked');
            }, 600);
        }
        navigate(`/products/view/${productUuid}`);
    };

    const handleAddProductClick = () => {
        navigate('/add-post');
    };

    return (
        <div className="product-page-container">
            <div className="ad-section">
                {loading ? (
                    <CarouselSkeleton />
                ) : (
                    promotedProducts.length > 0 && (
                        <PremiumCarousel
                            products={promotedProducts}
                            onProductClick={handleProductClick}
                            handleLike={handleLike}
                            loggedInUser={loggedInUser}
                            title="Produits Premium"
                            subtitle="Sélection premium de ce vendeur"
                        />
                    )
                )}
            </div>

            {/* Add Product Filters */}
            {!loading && allProducts.length > 0 && (
                <ProductFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    productsCount={filteredProducts.length}
                    totalProducts={allProducts.length}
                />
            )}

            <div className="main-content">
                <h2 className="my-product">Mes Produits</h2>
                <button
                    className="add-product-button"
                    onClick={handleAddProductClick}
                    style={{ float: 'right', fontSize: '0.9rem' }}
                >
                    <i className="fas fa-plus-circle"></i> Ajouter un produit
                </button>
                {loading ? (
                    <ProductGridSkeleton />
                ) : (
                    <ProductsGrid
                        products={products}
                        likedProducts={products.filter(product => product.liked_by?.includes(loggedInUser?.id)).map(product => product.id)}
                        loggedInUser={loggedInUser}
                        handleProductClick={handleProductClick}
                        handleLikeClick={handleLike}
                        hasMore={hasMore}
                        fetchMoreData={fetchMoreData}
                        loadingMore={loadingProducts}
                    />
                )}
            </div>
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={handleNotificationClose}
                />
            )}
        </div>
    );
};

export default ProductsUser;